import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Droplets } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { verifyLoginSession } from "@/utils/verifyLoginSession";
import { readReturnPath } from "@/utils/safeReturnPath";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setUser, setSessionToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Check for session_id in hash, search params, or pathname
        const hash = window.location.hash;
        const search = window.location.search;

        // Phase 7g.15.67 fix #6 — Emergent may redirect with an
        // OAuth-style error envelope (`?error=invalid_request&
        // error_description=Invalid+state+parameter`).  Detect it
        // BEFORE looking for session_id so the customer gets a clear
        // retry message instead of a silent "Authentication failed".
        const errParam = (() => {
          const sp = new URLSearchParams(search);
          if (sp.get("error")) return { error: sp.get("error"), desc: sp.get("error_description") || "" };
          const hp = new URLSearchParams(hash.replace(/^#/, ""));
          if (hp.get("error")) return { error: hp.get("error"), desc: hp.get("error_description") || "" };
          return null;
        })();
        if (errParam) {
          console.error("[AuthCallback] OAuth error:", errParam);
          if (
            errParam.error === "invalid_request" &&
            /invalid state parameter/i.test(errParam.desc || "")
          ) {
            toast.error(t("auth_callback.took_too_long"));
          } else {
            toast.error(errParam.desc || t("auth_callback.failed_retry"));
          }
          navigate("/welcome", { replace: true });
          return;
        }

        // Exact parameter names, decoded once (including native bridge redirects).
        const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id")
          || new URLSearchParams(search).get("session_id");

        if (!sessionId) {
          console.error("No session_id found in URL");
          toast.error(t("auth_callback.failed_retry"));
          navigate("/welcome");
          return;
        }
        

        // Exchange session_id for session_token
        const response = await apiClient.post("/auth/session", {
          session_id: sessionId,
        });

        const { user: userData, token } = await verifyLoginSession(apiClient, response.data);
        // Do not publish identity from the exchange envelope or a cached cookie.
        sessionStorage.setItem('session_token', token);
        setUser(userData, true);  // Flag as login to prevent checkAuth race
        setSessionToken(token);
        // Identity caching is best-effort; authorization remains server-verified.
        try { localStorage.setItem("user", JSON.stringify(userData)); } catch { /* optional cache */ }

        // Native (Capacitor) — dismiss the in-app Custom Tab /
        // SFSafariViewController that the user signed in through, so
        // they land back inside the WashPros app (not stuck on the
        // browser overlay). No-op on web.
        try {
          const { closeBrowser } = await import("@/native/browser");
          await closeBrowser();
        } catch (_) {
          // Browser plugin missing or already closed — safe to ignore
        }

        // Phase 7g.15.138 (2026-06-20, P0 Android auth hardening per Rick directive) —
        // Same flush rationale as PremiumAuthPage.completeLoginAndRedirect:
        // the sessionStorage.setItem above is wrapped to async-mirror to
        // Capacitor Preferences; awaiting the flush here makes the
        // Google/OAuth login a durable checkpoint before navigate().
        try {
          const platformMod = await import("@/native/platform");
          if (platformMod?.isNative?.()) {
            const bootstrapMod = await import("@/native/bootstrap");
            await bootstrapMod.flushNativePreferences?.();
          }
        } catch (_e) { /* never block login */ }

        // Check if user needs to accept agreement (from WelcomeScreen)
        const pendingAgreement = sessionStorage.getItem('pendingAgreement');
        const agreementVersion = sessionStorage.getItem('agreementVersion') || '1.0';
        
        if (pendingAgreement === 'true') {
          try {
            // Submit agreement acceptance to backend
            await apiClient.post("/auth/accept-agreement", {
              version: agreementVersion,
              accepted_terms: true,
              accepted_privacy: true,
              accepted_billing: true
            });
            // Retain pending consent after failure so a retry can record it.
            sessionStorage.removeItem('pendingAgreement');
            sessionStorage.removeItem('agreementVersion');
          } catch (agreementError) {
            console.error("Failed to record agreement:", agreementError);
            // Continue anyway - user can accept again later if needed
          }
        }

        // Check for intended path (from portal selection or booking flow)
        const intendedPath = readReturnPath(null, null);
        if (intendedPath) {
          try { localStorage.removeItem('intendedPath'); } catch { /* optional hint */ }
          try { sessionStorage.removeItem('intendedPath'); } catch { /* optional hint */ }
          navigate(intendedPath, { 
            replace: true, 
            state: { user: userData } 
          });
        } else {
          // Role-based redirect when no explicit intended path
          const role = userData?.role;
          sessionStorage.setItem("login_timestamp", Date.now().toString());
          if (["admin", "master_admin", "super_admin"].includes(role)) {
            navigate("/admin", { replace: true, state: { user: userData } });
          } else if (role === "vendor") {
            navigate("/vendor", { replace: true, state: { user: userData } });
          } else {
            navigate("/dashboard", { 
              replace: true, 
              state: { user: userData } 
            });
          }
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        // Even on error, try to preserve booking state by redirecting to intendedPath
        let intendedPath = null;
        try { intendedPath = sessionStorage.getItem('intendedPath'); } catch { /* storage disabled */ }
        try { intendedPath = localStorage.getItem('intendedPath') || intendedPath; } catch { /* storage disabled */ }
        if (intendedPath) {
          // Don't clear — user may need to try again after logging in via email
          toast.error(t("auth_callback.didnt_complete"));
          navigate("/auth/login", { replace: true });
        } else {
          toast.error(t("auth_callback.failed_retry"));
          navigate("/welcome", { replace: true });
        }
      }
    };

    processAuth();
  }, [navigate, setUser, setSessionToken, t]);

  return (
    <div data-testid="auth-callback-progress" role="status" className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center animate-pulse">
          <Droplets className="w-8 h-8 text-white" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">{t("auth_callback.signing_in")}</h2>
          <p className="text-slate-400">{t("auth_callback.please_wait")}</p>
        </div>
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={`item-${i}`}
              className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
